import {
  Flatten,
  RouteToTemplate,
  SettingsRouteLabelKey,
} from '@rematter/modules/settings/_types';
import { Col, cn } from '@rematter/ui-web';
import {
  LucideBell,
  LucideCoins,
  LucideCpu,
  LucideFileBarChart,
  LucideGrid2X2Plus,
  LucideHistory,
  LucideLock,
  LucideMapPin,
  LucideMonitor,
  LucidePackage,
  LucideShieldBan,
  LucideShieldCheck,
  LucideSlidersHorizontal,
  LucideStore,
  LucideTag,
  LucideUser,
  LucideUsers,
} from 'lucide-react';

import { ComplianceRulesRootView } from './_components/compliance/_ComplianceRulesRootView';
import { DispatchHistoryListView } from './_components/compliance/_DispatchHistoryListView';
import { DoNotBuyListView } from './_components/compliance/_DoNotBuyListView';
import { FacilityRulesView } from './_components/compliance/_FacilityRulesView';
import { GovReportingConfigDetailView } from './_components/compliance/_GovReportingConfigDetailView';
import { GovReportingConfigView } from './_components/compliance/_GovReportingConfigView';
import { ItemJurisdictionCodesView } from './_components/compliance/_ItemJurisdictionCodesView';
import { CustomAttributesView } from './_components/custom-attributes/_CustomAttributesView';
import { CustomerPortalRoleDetailsView } from './_components/customer-portal/_CustomerPortalRoleDetailsView';
import { CustomerPortalRootView } from './_components/customer-portal/_CustomerPortalRootView';
import { CustomerPortalUserDetailsView } from './_components/customer-portal/_CustomerPortalUserDetailsView';
import { HardwareManagementView } from './_components/hardware/_HardwareManagementView';
import { CameraDetailView } from './_components/hardware/cameras/_CameraDetailView';
import { PrinterDetailView } from './_components/hardware/printers/_PrinterDetailView';
import { ScaleDetailView } from './_components/hardware/scales/_ScaleDetailView';
import { ScannerDetailView } from './_components/hardware/scanners/_ScannerDetailView';
import { SidecarDetailView } from './_components/hardware/sidecars/_SidecarDetailView';
import { SidecarRegisterView } from './_components/hardware/sidecars/_SidecarRegisterView';
import { InventoryManagementView } from './_components/inventory/_InventoryManagementView';
import { CategoryDetailView } from './_components/inventory/categories/_CategoryDetailView';
import { ItemAliasDetailView } from './_components/inventory/item-aliases/_ItemAliasDetailView';
import { ItemGroupDetailView } from './_components/inventory/item-groups/_ItemGroupDetailView';
import { ItemDetailView } from './_components/inventory/items/_ItemDetailView';
import { LocationDetailsView } from './_components/locations/_LocationDetailsView';
import { LocationGroupDetailsView } from './_components/locations/_LocationGroupDetailsView';
import { LocationsRootView } from './_components/locations/_LocationsRootView';
import { NotificationsView } from './_components/notifications/_NotificationsView';
import { PreferencesView } from './_components/preferences/_PreferencesView';
import { RoleDetailsView } from './_components/team-and-permissions/_RoleDetailsView';
import { TeamPermissionsFacilityDetailsView } from './_components/team-and-permissions/_TeamPermissionsFacilityDetailsView';
import { TeamPermissionsRootView } from './_components/team-and-permissions/_TeamPermissionsRootView';
import { UserDetailsView } from './_components/team-and-permissions/_UserDetailsView';
import { MenuItemGroup } from './_types';
import { useTranslations } from './i18n/useTranslations';

type NotImplementedViewProps = {
  labelKey: SettingsRouteLabelKey;
};

const NotImplementedView = ({ labelKey }: NotImplementedViewProps) => {
  const t = useTranslations();
  return (
    <Col className={cn('@NotImplementedView', 'size-full p-5')} xCenter yCenter>
      <div className={'text-lg font-medium'}>
        {t.settingsLayout.title}{' '}
        <span className={'font-light opacity-50'}>/</span>{' '}
        {t.settingsLayout.routeLabels[labelKey]}
      </div>
      <div className={'opacity-50'}>{t.settingsLayout.notImplemented}</div>
    </Col>
  );
};

export const settingsRouteGroups = [
  {
    groupTitle: { i18nKey: 'personal' },
    items: [
      {
        pattern: '/profile',
        label: { i18nKey: 'profile' },
        icon: LucideUser,
        component: () => <NotImplementedView labelKey="profile" />,
        disabled: true,
      },
      {
        pattern: '/access-and-security',
        label: { i18nKey: 'accessAndSecurity' },
        icon: LucideLock,
        component: () => <NotImplementedView labelKey="accessAndSecurity" />,
        disabled: true,
      },
      {
        pattern: '/preferences',
        label: { i18nKey: 'preferences' },
        icon: LucideSlidersHorizontal,
        component: PreferencesView,
      },
      {
        pattern: '/notifications',
        label: { i18nKey: 'notifications' },
        icon: LucideBell,
        component: NotificationsView,
      },
    ],
  },
  {
    groupTitle: { i18nKey: 'organization' },
    items: [
      {
        pattern: ['/locations/facilities', '/locations/groups'] as const,
        label: { i18nKey: 'locations' },
        icon: LucideMapPin,
        component: LocationsRootView,
      },
      {
        pattern: [
          '/locations/facilities/:facilityId/info',
          '/locations/facilities/:facilityId/preferences',
          '/locations/facilities/:facilityId/document-templates',
          '/locations/facilities/:facilityId/freight',
        ],
        label: { i18nKey: 'facilityDetails' },
        component: LocationDetailsView,
        hideFromMenu: true,
      },
      {
        pattern: '/locations/groups/:groupId',
        label: { i18nKey: 'groupDetails' },
        component: LocationGroupDetailsView,
        hideFromMenu: true,
      },
      {
        pattern: [
          '/team-and-permissions',
          '/team-and-permissions/facilities',
          '/team-and-permissions/roles',
          '/team-and-permissions/users',
        ] as const,
        label: { i18nKey: 'teamAndPermissions' },
        icon: LucideUsers,
        component: TeamPermissionsRootView,
      },
      {
        pattern: '/team-and-permissions/users/:userId',
        label: { i18nKey: 'userDetails' },
        component: UserDetailsView,
        hideFromMenu: true,
      },
      {
        pattern: [
          '/team-and-permissions/roles/:roleId',
          '/team-and-permissions/roles/:roleId/permissions',
          '/team-and-permissions/roles/:roleId/users',
        ],
        label: { i18nKey: 'roleDetails' },
        component: RoleDetailsView,
        hideFromMenu: true,
      },
      {
        pattern: '/team-and-permissions/facilities/:facilityId',
        label: { i18nKey: 'facilityDetails' },
        component: TeamPermissionsFacilityDetailsView,
        hideFromMenu: true,
      },
    ],
  },
  {
    groupTitle: { i18nKey: 'modules' },
    items: [
      {
        pattern: '/inventory',
        label: { i18nKey: 'inventory' },
        icon: LucidePackage,
        component: InventoryManagementView,
      },
      {
        pattern: '/inventory/items/:id',
        label: { i18nKey: 'itemDetails' },
        component: ItemDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/inventory/item-groups/:id',
        label: { i18nKey: 'itemGroupDetails' },
        component: ItemGroupDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/inventory/item-aliases/:id',
        label: { i18nKey: 'itemAliasDetails' },
        component: ItemAliasDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/inventory/categories/:id',
        label: { i18nKey: 'categoryDetails' },
        component: CategoryDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/hardware',
        label: { i18nKey: 'hardware' },
        icon: LucideCpu,
        component: HardwareManagementView,
      },
      {
        pattern: '/hardware/sidecars/register',
        label: { i18nKey: 'registerSidecar' },
        component: SidecarRegisterView,
        hideFromMenu: true,
      },
      {
        pattern: '/hardware/sidecars/:id',
        label: { i18nKey: 'sidecarDetails' },
        component: SidecarDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/hardware/cameras/:id',
        label: { i18nKey: 'cameraDetails' },
        component: CameraDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/hardware/scanners/:sidecarId/:scannerId',
        label: { i18nKey: 'scannerDetails' },
        component: ScannerDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/hardware/printers/:sidecarId/:printerId',
        label: { i18nKey: 'printerDetails' },
        component: PrinterDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/hardware/scales/:sidecarId/:scaleId',
        label: { i18nKey: 'scaleDetails' },
        component: ScaleDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/sales',
        label: { i18nKey: 'sales' },
        icon: LucideStore,
        component: () => <NotImplementedView labelKey="sales" />,
        disabled: true,
      },
      {
        pattern: '/finance',
        label: { i18nKey: 'finance' },
        icon: LucideCoins,
        component: () => <NotImplementedView labelKey="finance" />,
        disabled: true,
      },
    ],
  },
  {
    groupTitle: { i18nKey: 'compliance' },
    items: [
      {
        pattern: '/compliance/rules',
        label: { i18nKey: 'ruleConfiguration' },
        icon: LucideShieldCheck,
        component: ComplianceRulesRootView,
      },
      {
        pattern: '/compliance/rules/:facilityId',
        label: { i18nKey: 'facilityRules' },
        component: FacilityRulesView,
        hideFromMenu: true,
      },
      {
        pattern: '/compliance/do-not-buy',
        label: { i18nKey: 'doNotBuyList' },
        icon: LucideShieldBan,
        component: DoNotBuyListView,
      },
      {
        pattern: '/compliance/reporting',
        label: { i18nKey: 'govReporting' },
        icon: LucideFileBarChart,
        component: GovReportingConfigView,
      },
      {
        pattern: '/compliance/reporting/:configId',
        label: { i18nKey: 'configDetail' },
        component: GovReportingConfigDetailView,
        hideFromMenu: true,
      },
      {
        pattern: '/compliance/item-codes',
        label: { i18nKey: 'itemCodes' },
        icon: LucideTag,
        component: ItemJurisdictionCodesView,
      },
      {
        pattern: '/compliance/dispatch-history',
        label: { i18nKey: 'dispatchHistory' },
        icon: LucideHistory,
        component: DispatchHistoryListView,
      },
    ],
  },
  {
    items: [
      {
        pattern: '/custom-attributes',
        label: { i18nKey: 'customAttributes' },
        icon: LucideGrid2X2Plus,
        component: CustomAttributesView,
      },
      {
        pattern: [
          '/customer-portal',
          '/customer-portal/roles',
          '/customer-portal/users',
        ] as const,
        label: { i18nKey: 'customerPortal' },
        icon: LucideMonitor,
        component: CustomerPortalRootView,
        hideFromMenu: true,
      },
      {
        pattern: '/customer-portal/users/:invitationId',
        label: { i18nKey: 'customerDetails' },
        component: CustomerPortalUserDetailsView,
        hideFromMenu: true,
      },
      {
        pattern: [
          '/customer-portal/roles/:roleId',
          '/customer-portal/roles/:roleId/permissions',
          '/customer-portal/roles/:roleId/users',
        ],
        label: { i18nKey: 'roleDetails' },
        component: CustomerPortalRoleDetailsView,
        hideFromMenu: true,
      },
    ],
  },
] as const satisfies MenuItemGroup[];

export type SettingsPath =
  | RouteToTemplate<
      Flatten<(typeof settingsRouteGroups)[number]['items'][number]['pattern']>
    >
  | '/';
